/**
 * Envoltorios tipados de las Cloud Functions "callable".
 * El frontend nunca escribe Firestore directamente: toda mutación pasa por aquí.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type {
  CrearExpedienteInput,
  SolicitarPrestamoInput,
  AceptarPrestamoInput,
  EndosarPrestamoInput,
  DevolverPrestamoInput,
  CancelarPrestamoInput,
  EntregaTurnoInput,
  CambiarUbicacionInput,
  CambioOficinaInput,
  GestionMasivaInput,
  EditarExpedienteInput,
  ForzarDevolucionInput,
  GestionarAccesoInput,
  AccesoMasivoInput,
  ClaveKioscoInput,
  GuardarOficinaInput,
  CrearUsuarioNativoInput,
  CrearOtroDocumentoInput,
  AceptarOtroDocumentoInput,
  ReporteTurnoInput,
  CerrarTurnoInput,
} from '@cji/shared';

/** Una fila del reporte de turno, por prestador. */
export interface FilaPrestador {
  email: string;
  prestadas: number;
  devueltas: number;
  pendientesDevolucion: number;
  endosadas: number;
  documentosRecibidos: number;
  foliosRecibidos: number;
}

export interface Reporte {
  desde: string;
  hasta: string;
  totales: {
    prestadas: number; devueltas: number; endosadas: number;
    documentosRecibidos: number; foliosRecibidos: number;
  };
  prestadores: FilaPrestador[];
}

/** Timeout por defecto del SDK: 70 s. Insuficiente para operaciones masivas. */
const TIMEOUT_LARGO = 540_000;

function llamar<TInput, TOut>(nombre: string, timeout?: number) {
  const fn = httpsCallable<TInput, TOut>(functions, nombre, timeout ? { timeout } : undefined);
  return async (data: TInput): Promise<TOut> => (await fn(data)).data;
}

export const api = {
  crearExpediente: llamar<CrearExpedienteInput, { ok: boolean; expedienteId: string }>('crearExpediente'),
  gestionMasiva: llamar<GestionMasivaInput, { ok: boolean; creados: number; omitidos: string[] }>('gestionMasiva'),
  cambiarUbicacion: llamar<CambiarUbicacionInput, { ok: boolean }>('cambiarUbicacion'),
  cambioOficina: llamar<CambioOficinaInput, { ok: boolean; actualizados: number; errores: string[] }>('cambioOficina'),

  solicitarPrestamo: llamar<SolicitarPrestamoInput, { ok: boolean; prestamoId: string }>('solicitarPrestamo'),
  aceptarPrestamo: llamar<AceptarPrestamoInput, { ok: boolean }>('aceptarPrestamo'),
  endosarPrestamo: llamar<EndosarPrestamoInput, { ok: boolean; nuevoPrestamoId: string }>('endosarPrestamo'),
  devolverPrestamo: llamar<DevolverPrestamoInput, { ok: boolean }>('devolverPrestamo'),
  cancelarPrestamo: llamar<CancelarPrestamoInput, { ok: boolean }>('cancelarPrestamo'),
  entregaTurno: llamar<EntregaTurnoInput, { ok: boolean; traspasadas: number; errores: string[] }>('entregaTurno'),

  provisionarUsuario: llamar<Record<string, never>, { ok: boolean; creado: boolean }>('provisionarUsuario'),

  // Documentos anexos a una carpeta
  crearOtroDocumento: llamar<CrearOtroDocumentoInput, { ok: boolean; otroId: string; radicadoOtro: string }>('crearOtroDocumento'),
  aceptarOtroDocumento: llamar<AceptarOtroDocumentoInput, { ok: boolean }>('aceptarOtroDocumento'),

  // Reportes y turnos
  reporteTurno: llamar<ReporteTurnoInput, { ok: boolean; reporte: Reporte }>('reporteTurno', TIMEOUT_LARGO),
  cerrarTurno: llamar<CerrarTurnoInput, { ok: boolean; turnoId: string; reporte: Reporte; correoEncolado: boolean; destinatarios: string[] }>('cerrarTurno', TIMEOUT_LARGO),

  // Administración (solo admin)
  editarExpediente: llamar<EditarExpedienteInput, { ok: boolean }>('editarExpediente'),
  forzarDevolucion: llamar<ForzarDevolucionInput, { ok: boolean }>('forzarDevolucion'),
  gestionarAcceso: llamar<GestionarAccesoInput, { ok: boolean }>('gestionarAcceso'),
  guardarOficina: llamar<GuardarOficinaInput, { ok: boolean; id: string }>('guardarOficina'),
  accesoMasivo: llamar<AccesoMasivoInput, { ok: boolean; cambiados: number; total: number }>('accesoMasivo', TIMEOUT_LARGO),
  claveKiosco: llamar<ClaveKioscoInput, { ok: boolean }>('claveKiosco'),
  crearUsuarioNativo: llamar<CrearUsuarioNativoInput, { ok: boolean; email: string; alias: string }>('crearUsuarioNativo'),
  generarClavesKiosco: llamar<Record<string, never>, { ok: boolean; actualizados: number; creados: number; total: number; errores: string[] }>('generarClavesKiosco', TIMEOUT_LARGO),
};
